import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="toast"
export default class extends Controller {
  static values = {
    message: String,
    type: { type: String, default: "info" },
    duration: { type: Number, default: 5000 },
    persistent: { type: Boolean, default: false }
  }
  
  static targets = ["container"]

  connect() {
    if (!this.hasContainerTarget) {
      this.createContainer()
    }
    
    if (this.hasMessageValue) {
      this.show(this.messageValue, this.typeValue, this.durationValue)
    }
  }

  createContainer() {
    if (!document.querySelector('.toast-container')) {
      const container = document.createElement('div')
      container.className = 'toast-container fixed top-4 right-4 z-50 space-y-2'
      document.body.appendChild(container)
    }
  }

  show(message, type = 'info', duration = 5000) {
    const container = document.querySelector('.toast-container')
    if (!container) this.createContainer()
    
    const toast = this.createToast(message, type)
    container.appendChild(toast)
    
    // Animate in
    requestAnimationFrame(() => {
      toast.classList.remove('translate-x-full', 'opacity-0')
      toast.classList.add('translate-x-0', 'opacity-100')
    })
    
    // Auto dismiss unless persistent
    if (!this.persistentValue && duration > 0) {
      setTimeout(() => {
        this.dismiss(toast)
      }, duration)
    }
    
    return toast
  }

  createToast(message, type) {
    const toast = document.createElement('div')
    toast.className = `toast transform translate-x-full opacity-0 transition-all duration-300 ease-in-out max-w-sm w-full bg-portal-surface border border-portal-border shadow-2xl rounded-lg pointer-events-auto flex ${this.getTypeClasses(type)}`
    
    toast.innerHTML = `
      <div class="flex-1 w-0 p-4">
        <div class="flex items-start">
          <div class="flex-shrink-0">
            ${this.getTypeIcon(type)}
          </div>
          <div class="ml-3 flex-1">
            <p class="text-sm font-medium text-white">
              ${message}
            </p>
          </div>
        </div>
      </div>
      <div class="flex border-l border-portal-border">
        <button onclick="this.closest('.toast').dispatchEvent(new CustomEvent('dismiss'))" 
                class="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-gray-400 hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-gold-500">
          <i class="fas fa-times"></i>
        </button>
      </div>
    `
    
    // Add dismiss event listener
    toast.addEventListener('dismiss', () => this.dismiss(toast))
    
    return toast
  }

  dismiss(toast) {
    if (!toast) return
    
    toast.classList.remove('translate-x-0', 'opacity-100')
    toast.classList.add('translate-x-full', 'opacity-0')
    
    setTimeout(() => {
      if (toast.parentNode) {
        toast.parentNode.removeChild(toast)
      }
    }, 300)
  }

  getTypeClasses(type) {
    switch(type) {
      case 'success':
        return 'border-l-4 border-green-400 bg-green-500/5'
      case 'error':
        return 'border-l-4 border-red-400 bg-red-500/5'
      case 'warning':
        return 'border-l-4 border-yellow-400 bg-yellow-500/5'
      case 'info':
      default:
        return 'border-l-4 border-gold-400 bg-gold-500/5'
    }
  }

  getTypeIcon(type) {
    switch(type) {
      case 'success':
        return '<i class="fas fa-check-circle text-green-400"></i>'
      case 'error':
        return '<i class="fas fa-exclamation-circle text-red-400"></i>'
      case 'warning':
        return '<i class="fas fa-exclamation-triangle text-yellow-400"></i>'
      case 'info':
      default:
        return '<i class="fas fa-info-circle text-blue-400"></i>'
    }
  }

  // Static methods for easy use throughout the app
  static success(message, duration = 5000) {
    const controller = new this()
    controller.createContainer()
    return controller.show(message, 'success', duration)
  }

  static error(message, duration = 7000) {
    const controller = new this()
    controller.createContainer()
    return controller.show(message, 'error', duration)
  }

  static warning(message, duration = 6000) {
    const controller = new this()
    controller.createContainer()
    return controller.show(message, 'warning', duration)
  }

  static info(message, duration = 5000) {
    const controller = new this()
    controller.createContainer()
    return controller.show(message, 'info', duration)
  }
}

// Make toast methods available globally
window.Toast = {
  success: (message, duration) => ToastController.success(message, duration),
  error: (message, duration) => ToastController.error(message, duration),
  warning: (message, duration) => ToastController.warning(message, duration),
  info: (message, duration) => ToastController.info(message, duration)
}